import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { CultureService } from '../../../shared/services/culture/culture.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { UserProfile } from '../../../shared/interfaces/role.interface';

@Component({
  selector: 'app-organization',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    TitleComponent,
    RouterOutlet,
    LoaderComponent,
  ],
  templateUrl: './organization.component.html',
  styleUrls: [
    './organization.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class OrganizationComponent implements OnDestroy {
  title: string = 'Organigrama';
  subtitle: string =
    'Gestiona las imágenes de la organización y la toma de decisiones.';
  myroles: any = {}; // Change from array to object

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;

  get isLoading() {
    return this.loaderService.getLoader();
  }

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private cultureService: CultureService,
    private storageService: StorageService
  ) {}

  readonly dialog = inject(MatDialog);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = ['name', 'image', 'acciones'];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.setupCustomSorting();
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar esta misión/visión?',
        message: 'Se eliminará el siguiente organigrama: ' + nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return this.cultureService.deleteOrganization(id);
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar la tabla
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dataSource.data = this.dataSource.data.filter(
          (user) => user.id !== id
        );
      }
    });
  }

  ngOnInit() {
    this.loaderService.showLoader();

    // Setup navigation listener
    this.setupNavigationListener();

    // Load culture data
    this.loadCultureData();
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (
          this.router.url.includes('/culture/organization/edit') ||
          this.router.url.includes('/culture/organization/add')
        ) {
          this.isUuid = true;
        } else if (this.router.url.includes('/culture/organization')) {
          this.isUuid = false;

          // Clear existing subscriptions before reloading data
          this.clearSubscriptions();

          // Reload data
          this.loadCultureData();
        }

        this.cdr.detectChanges();
      });
  }

  loadCultureData() {
    const cultureSub = this.cultureService.getOrganizations().subscribe({
      next: (data) => {
        // console.debug('Culture data:', data);
        this.dataSource.data = data;
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        // Set up custom sorting
        this.setupCustomSorting();

        // Set up the filter predicate for date filtering
        this.setupFilterPredicate();

        // Load roles
        this.loadRoles();

        this.loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Error fetching culture data:', error);
        this.loaderService.hideLoader();
      },
    });

    this.subscriptions.push(cultureSub);
  }

  setupFilterPredicate() {
    // Set up the filter predicate for date filtering
    this.dataSource.filterPredicate = (data, filter) => {
      const filterObj = JSON.parse(filter);
      let dateMatch = true;

      // Check date filter
      if (filterObj.dateFilter) {
        const createdWhen = new Date(data.created_when).getTime();
        dateMatch = createdWhen >= filterObj.dateFilter;
      }

      return dateMatch;
    };
  }

  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'organizational_chart',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      // console.log(
      //   'No user roles found in storage or no permissions for Cultura empresarial'
      // );
    }
  }

  clearSubscriptions() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Reset the subscriptions array
    this.subscriptions = [];
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.clearSubscriptions();

    // Also clean up navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
  }

  // Set up custom sort functions for the table columns
  private setupCustomSorting() {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'is_vision':
          // Sort by boolean value
          return item.is_vision ? 1 : 0;

        case 'description':
          // Sort by description text length
          return item.description?.length || 0;

        default:
          return item[property];
      }
    };
  }

  isUuid: boolean = false;

  goToOrganization(element: any) {
    this.router.navigate(['home/culture/organization/edit', element.id]);
    this.isUuid = true;
  }

  goToNewOrganization() {
    this.router.navigate(['home/culture/organization/add']);
    this.isUuid = true;
  }
}
